<template>
  <MRow :gutter="0" class="mb-4">
    <MCol :size="12">
      <MRow :gutter="0" class="flex-1">
        <MCol
          v-for="field in fieldDetails.fields"
          :key="field.key"
          :size="filedSize"
        >
          <MRow :gutter="0">
            <MCol class="mx-2">
              <FlotoFormItem
                v-if="field.inputType === 'text'"
                :id="field.name"
                v-model="formData[field.paramName]"
                :label="field.name"
                :placeholder="field.name"
                :rules="validationRules(field)"
                :disabled="disabled"
              />
              <FlotoFormItem
                v-else-if="field.inputType === 'textarea'"
                :id="field.name"
                v-model="formData[field.paramName]"
                type="textarea"
                :label="field.name"
                :placeholder="field.name"
                rules="description"
                :disabled="disabled"
              />
              <FlotoFormItem
                v-else-if="field.inputType === 'hardwareAssetPicker'"
                id="hardware-asset-picker"
                :label="`${$tc('parent_vm_host')}`"
              >
                <AssetPickerDrawer
                  v-model="formData[field.paramName]"
                  as-input
                  :module-name="moduleName"
                  :disabled="disabled"
                  :fetch-fn="fetchData"
                />
              </FlotoFormItem>
              <FlotoFormItem v-else :label="field.name">
                <div
                  v-if="field.inputType === 'number'"
                  :class="{ flex: field.unit, 'w-full': field.unit }"
                >
                  <MInputGroup
                    v-if="field.unit || field.currency"
                    compact
                    class="input-group-container"
                  >
                    <div :class="field.currency ? '' : 'flex-1'">
                      <MInputNumber
                        :id="field.name"
                        v-model="formData[field.paramName]"
                        :disabled="disabled"
                        :precision="field.precision ? field.precision : 0"
                        :rules="validationRules(field)"
                      />
                    </div>
                    <div
                      v-if="field.unit"
                      class="px-2 bg-neutral-lightest flex items-center justify-center text-neutral border-solid border-neutral-lighter border-l-0 border-t border-b border-r rounded rounded-tl-none rounded-bl-none"
                      style="display: inline-flex"
                    >
                      {{ $tc(field.unit) }}
                    </div>
                    <CurrencyPicker
                      v-if="field.currency"
                      v-model="formData.currencyId"
                      :allow-clear="false"
                    />
                  </MInputGroup>
                  <!-- <FlotoFormItem
                    v-else
                    :id="field.name"
                    v-model="formData[field.paramName]"
                    type="number"
                    :validation-label="field.name"
                    :rules="validationRules(field)"
                    :disabled="disabled"
                  /> -->
                  <MInputNumber
                    v-else
                    v-model="formData[field.paramName]"
                    :disabled="disabled"
                    :precision="field.precision ? field.precision : 0"
                    :rules="validationRules(field)"
                  />
                </div>
                <TimePicker
                  v-if="field.inputType === 'timePicker'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  :unit="field.unit ? field.unit : 'minutes'"
                  :disabled="disabled"
                />
                <BytesPicker
                  v-if="field.inputType === 'bytes'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  :unit="field.unit ? field.unit : 'gb'"
                  :disabled="disabled"
                />
                <HertzPicker
                  v-if="field.inputType === 'hertz'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  :unit="field.unit ? field.unit : 'ghz'"
                  :disabled="disabled"
                />
                <EnumDropdownPicker
                  v-if="field.inputType === 'boolean'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  as-input
                  :disabled="disabled"
                  :allow-clear="false"
                  :unassigned-value="false"
                  :options="
                    field.contextOptionKey
                      ? propertyComponentContext[field.contextOptionKey]
                      : propertyComponentContext.booleanOptions
                  "
                />
                <EnumDropdownPicker
                  v-if="field.inputType === 'dropdown'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  searchable
                  as-input
                  :disabled="disabled"
                  :options="propertyComponentContext[field.contextOptionKey]"
                />
                <FlotoTagsPicker
                  v-if="field.inputType === 'tags'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  type="multiStringValue"
                  :title="field.name"
                  :auto-complete-search="false"
                  :disabled="disabled"
                  :rules="validationRules(field)"
                />
                <FlotoDatePicker
                  v-if="
                    field.inputType === 'dateTime' || field.inputType === 'date'
                  "
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  :show-time="field.inputType === 'dateTime'"
                  as-input
                  :min-date="
                    type === 'storage_bucket_component'
                      ? null
                      : Moment().valueOf()
                  "
                  :disabled="disabled"
                />
                <ProductPicker
                  v-if="field.inputType === 'product'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  as-input
                  :disabled="disabled"
                />
                <VendorPicker
                  v-if="field.inputType === 'vendor'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  as-input
                  :disabled="disabled"
                />
                <ManufacturerPicker
                  v-if="field.inputType === 'manufacturer'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  as-input
                  :disabled="disabled"
                />
                <SoftwareLicenseTypePicker
                  v-if="field.inputType === 'softwareLicenseType'"
                  :id="field.name"
                  v-model="formData[field.paramName]"
                  as-input
                  :disabled="disabled"
                />
              </FlotoFormItem>
            </MCol>
          </MRow>
        </MCol>
      </MRow>
    </MCol>
    <MCol v-if="customFields.length" :size="12">
      <MDivider />
      <MRow :gutter="0" class="flex-1">
        <MCol>
          <MRow :gutter="0">
            <FormConsumer
              v-if="customFields.length"
              v-model="formData.fieldValueDetails"
              :form-fields="customFields"
              :avoid-default-value="Boolean(formData.id)"
              :with-submit="false"
              :focus-event-brodcast="false"
            />
          </MRow>
        </MCol>
      </MRow>
    </MCol>
  </MRow>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import FormConsumer from '@components/form-consumer.vue'
import ProductPicker from '@components/data-picker/product-picker'
import VendorPicker from '@components/data-picker/vendor-picker'
import ManufacturerPicker from '@components/data-picker/manufacturer-picker'
import SoftwareLicenseTypePicker from '@components/data-picker/software-license-type-picker'
import BytesPicker from '@components/data-picker/bytes-picker'
import HertzPicker from '@components/data-picker/hertz-picker'
import TimePicker from '@components/data-picker/time-picker'
import EnumDropdownPicker from './enum-dropdown-picker'
import AssetPickerDrawer from '@components/data-picker/asset-picker-drawer'
import Moment from 'moment'
import { getHardwareAssetTypeApi } from '@modules/asset/asset-api'

export default {
  name: 'PropertyComponentForm',
  components: {
    ProductPicker,
    VendorPicker,
    ManufacturerPicker,
    FormConsumer,
    BytesPicker,
    HertzPicker,
    SoftwareLicenseTypePicker,
    EnumDropdownPicker,
    AssetPickerDrawer,
    TimePicker,
    CurrencyPicker,
  },
  inject: ['propertyComponentContext'],
  props: {
    fieldDetails: { type: Object, required: true },
    formData: {
      type: Object,
      required: true,
    },
    customFields: {
      type: Array,
      default() {
        return []
      },
    },
    type: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    rules: { type: String, default: undefined },
    filedSize: { type: Number, default: 6 },
    moduleName: { type: String, required: true },
  },
  data() {
    this.Moment = Moment
    this.unassignedValue = null
    return {}
  },
  methods: {
    validationRules(field) {
      let rules = {}
      if (field.inputType === 'text') {
        // override inputType text validation based on perticular defined field rules
        // If rules are blank OR define specific rules like 'subject', 'max' than apply those rules
        if (
          !field.rules ||
          (field.rules && !('subject' in field.rules || 'max' in field.rules))
        ) {
          // apply defult ruls for inputType text
          rules.short_text = true
        }
      }
      if (field.inputType === 'number') {
        rules.numeric = true
        rules.min_value = 0
      }
      if (field.rules) {
        rules = {
          ...rules,
          ...field.rules,
        }
      }
      return rules
    },
    fetchData(moduleName, criterias, limit, offset) {
      return getHardwareAssetTypeApi(moduleName, criterias, limit, offset)
    },
  },
}
</script>

<style lang="less" scoped>
.input-group-container {
  top: 3px !important;
  display: flex !important;
  .@{ant-prefix}-input-number {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>

<style lang="less">
.input-group-container {
  .duration-type {
    .@{ant-prefix}-select-selection {
      @apply bg-neutral-lightest text-neutral;
    }
  }
}
</style>
